import { CloseOutlined, FileExcelOutlined, InboxOutlined } from '@ant-design/icons'
import { Button, Flex, message, Tag, Upload } from 'antd'
import { DistributionImportFileValue } from 'Modules'
import type { UploadRequestOption } from 'rc-upload/lib/interface'

import {
  DISTRIBUTION_IMPORT_ACCEPT,
  DISTRIBUTION_IMPORT_ALLOWED_EXTENSIONS,
  DISTRIBUTION_IMPORT_MAX_SIZE_MB,
  DISTRIBUTION_MESSAGES,
} from 'constants/promo-code'
import { logError } from 'utils/logger'

import { downloadWhitelistTemplate, parseEmailsFromFile } from './import-file-utils'

interface ImportFileUploadProps {
  /** Parsed file value (bound by Form.Item). */
  value?: DistributionImportFileValue
  onChange?: (value?: DistributionImportFileValue) => void
  disabled?: boolean
}

/**
 * "Import file" whitelist box: upload one xlsx/xls, extract its emails
 * client-side, and let the admin remove individual emails or replace the file.
 */
export const ImportFileUpload = ({ value, onChange, disabled }: ImportFileUploadProps) => {
  const beforeUpload = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
    if (!DISTRIBUTION_IMPORT_ALLOWED_EXTENSIONS.includes(ext)) {
      message.error(DISTRIBUTION_MESSAGES.INVALID_FILE_TYPE)
      return Upload.LIST_IGNORE
    }
    if (file.size / 1024 / 1024 > DISTRIBUTION_IMPORT_MAX_SIZE_MB) {
      message.error(DISTRIBUTION_MESSAGES.FILE_TOO_LARGE)
      return Upload.LIST_IGNORE
    }
    return true
  }

  // Parse locally instead of uploading — the file is rebuilt from the kept
  // emails and submitted with Save & Send Email.
  const handleUpload = async ({ file, onSuccess, onError }: UploadRequestOption) => {
    try {
      const uploaded = file as File
      const emails = await parseEmailsFromFile(uploaded)
      if (!emails.length) {
        message.error(DISTRIBUTION_MESSAGES.NO_EMAILS_IN_FILE)
        onError?.(new Error(DISTRIBUTION_MESSAGES.NO_EMAILS_IN_FILE))
        return
      }
      onChange?.({ fileName: uploaded.name, emails })
      onSuccess?.('ok')
    } catch (error: any) {
      logError('Parse whitelist file failed', error)
      message.error('Failed to read the uploaded file')
      onError?.(error)
    }
  }

  const uploadProps = {
    multiple: false,
    accept: DISTRIBUTION_IMPORT_ACCEPT,
    showUploadList: false,
    beforeUpload,
    customRequest: handleUpload,
    disabled,
  }

  if (!value) {
    return (
      <Upload.Dragger {...uploadProps} className="whitelist-email__dragger">
        <Flex vertical align="center" gap={4}>
          <InboxOutlined style={{ fontSize: 28, color: '#9ca3af' }} />
          <div style={{ fontWeight: 600 }}>
            Drag &amp; drop an email list file, or click to browse
          </div>
          <div style={{ fontSize: 12, color: '#6a7282' }}>
            Supports .xlsx, .xls, .csv — max {DISTRIBUTION_IMPORT_MAX_SIZE_MB}MB ·{' '}
            <a
              onClick={(e) => {
                // Don't let the click bubble up to the Dragger (which opens the file dialog).
                e.preventDefault()
                e.stopPropagation()
                downloadWhitelistTemplate()
              }}
            >
              Download template
            </a>
          </div>
        </Flex>
      </Upload.Dragger>
    )
  }

  const removeEmail = (email: string) => {
    onChange?.({ ...value, emails: value.emails.filter((item) => item !== email) })
  }

  return (
    <div className="whitelist-email__import-summary">
      <Flex align="center" gap={8} wrap="wrap">
        <FileExcelOutlined style={{ color: '#008236' }} />
        <span className="whitelist-email__import-name">{value.fileName}</span>
        <span className="whitelist-email__import-count">
          {value.emails.length} email{value.emails.length === 1 ? '' : 's'}
        </span>
        {!disabled && (
          <>
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => onChange?.(undefined)}
            />
            <Upload {...uploadProps}>
              <Button type="link" size="small">
                Replace
              </Button>
            </Upload>
          </>
        )}
      </Flex>
      <div className="whitelist-email__import-emails">
        {value.emails.map((email) => (
          <Tag key={email} closable={!disabled} onClose={() => removeEmail(email)}>
            {email}
          </Tag>
        ))}
      </div>
    </div>
  )
}
